import * as React from "react";
import { connect } from "react-redux";
import { Subtract } from "utility-types";

import { loadedDefinedMap, asArrayValue } from "common/components/component-helpers";
import { AppDescription } from "common/components/SerializedApp";
import { DataStore, ForceValueProducer } from "common/components/types";
import { forceUserProfileRow, getUserProfileTableInfo, UserProfileRow } from "common/components/user-profile";
import { mapFilterUndefined } from "common/Support";
import { AppsAndState, getCurrentAppAndState, RootState } from "store/types";
import { SubscribingReactComponent } from "common/components/uis";
import { withAppID } from "designer/lib/with-app-id";
import { withAppEnvironment } from "hooks/use-app-environment";
import { ForwardingAppEnvironment } from "lib/forwarding-app-environment";

function makeUserProfileRows(
    appDescription: AppDescription,
    datastore: DataStore,
    force: ForceValueProducer
): readonly UserProfileRow[] | undefined {
    const info = getUserProfileTableInfo(appDescription);
    if (info === undefined) return undefined;

    const rows = loadedDefinedMap(force(datastore.fetchTableRows(info.tableName, true)), asArrayValue);
    if (rows === undefined) return undefined;

    return mapFilterUndefined(rows, (rowVP, index) => forceUserProfileRow(rowVP, index.toString(), info, force));
}

interface Props {
    readonly appID: string | undefined;
    readonly apps: AppsAndState;
    readonly appEnvironment: ForwardingAppEnvironment | undefined;
}
interface State {}

const UserProfilesContext = React.createContext<readonly UserProfileRow[]>([]);

class UserProfileRowProvider extends SubscribingReactComponent<React.PropsWithChildren<Props>, State> {
    public expandState() {
        return {};
    }

    public recompute(force: ForceValueProducer): React.ReactNode {
        const { appID, apps, appEnvironment } = this.props;

        if (appID === null) return null;

        const f = getCurrentAppAndState(appID, apps);
        if (f === undefined || appEnvironment === undefined) return null;

        const { serializedApp } = f;

        const users = makeUserProfileRows(serializedApp, appEnvironment.dataStore, force);
        return <UserProfilesContext.Provider value={users ?? []}>{this.props.children}</UserProfilesContext.Provider>;
    }
}

const mapState = (state: RootState) => ({
    apps: state.apps,
});

const Impl = withAppEnvironment(withAppID(connect(mapState)(UserProfileRowProvider)));

interface WithUserProfileProps {
    userProfiles: readonly UserProfileRow[];
}

export const withUserProfiles = <P extends WithUserProfileProps>(Component: React.ComponentType<P>) =>
    class WithUserProfiles extends React.PureComponent<Subtract<P, WithUserProfileProps>> {
        public render() {
            return (
                <Impl>
                    <UserProfilesContext.Consumer>
                        {userProfiles => <Component {...(this.props as P)} userProfiles={userProfiles} />}
                    </UserProfilesContext.Consumer>
                </Impl>
            );
        }
    };
